import React, { useState } from 'react'
import qs from 'qs'
import { Alert, Box, Button, Chip, Typography } from '@mui/material'
import { useToasts } from 'react-toast-notifications'
import { COST_CALCULATOR_URL } from 'src/participantHistory/financialEligibility/CostCalculatorLink'
import getCostEstimateValuesFromClipboard from 'src/participantHistory/financialEligibility/getCostEstimateValuesFromClipboard'
import { useLoadedNoteDraft } from 'src/noteDraft/useNoteDraft'
import { NoteDraftAlerts } from 'src/noteDraft/NoteDraftAlerts'
import {
  GetAdvancedEligibilityLookupInputsQuery,
  useAdvancedCostEstimateLookupLazyQuery,
  useGetAdvancedEligibilityLookupInputsQuery,
} from '../../../../api/advancedFinancialEligibilityLookup.generated'
import { describeFailure } from '../../../../api/failureReason'
import { useDraftSaver, useRestoredDraft, primeRestoredDraft } from '../../../../api/historyDraft'
import { useSubmitFinancialEligibilityHealthPlan } from '../../../../api/useSubmitPatientHistory'
import FailureMessage from '../../../../components/FailureMessage'
import { useDisabledInPreview } from '../../../../dashboard/panelPreviewContext'
import {
  FieldGrid,
  FieldSection,
  FormActions,
  LabeledCheckbox,
  LabeledDate,
  LabeledNumber,
  LabeledText,
  MissingFields,
  todayIso,
  useRequiredFields,
} from '../../../fieldPrimitives'
import PrefillLoading from '../PrefillLoading'
import { ValidationIssues } from '../validation'
import { getIn, textAt } from '../valuePath'
import HealthPlanLookupDialog, { EligibilityCheck, aperoEligibilityUrl, isoDay } from './HealthPlanLookupDialog'

const FORM_TYPE = 'FINANCIAL_ELIGIBILITY_HEALTH_PLAN'
const COVERAGE_INACTIVE = 'Coverage must be active'
const TOO_MANY_DECIMALS = 'The input allows maximum two decimal digits'

type Patient = GetAdvancedEligibilityLookupInputsQuery['patient'] | undefined

type Insurance = {
  provided: boolean
  coverageActive: boolean
  memberID: string
  groupDescription: string
  groupNumber: string
  firstname: string
  lastname: string
  birthDate: string
  aperoEligibilityID?: string
  warnings: string[]
}

const COST_FIELDS = [
  {
    key: 'intake',
    standardKey: 'intakeCostEstimate',
    label: 'Intake cost estimate ($)',
    descriptionLabel: 'Intake cost',
  },
  {
    key: 'month1',
    standardKey: 'month1CostEstimate',
    label: 'Month 1 cost estimate ($)',
    descriptionLabel: 'Month 1 cost',
  },
  {
    key: 'month2',
    standardKey: 'month2CostEstimate',
    label: 'Month 2 cost estimate ($)',
    descriptionLabel: 'Month 2 cost',
  },
  {
    key: 'month3',
    standardKey: 'month3CostEstimate',
    label: 'Month 3 cost estimate ($)',
    descriptionLabel: 'Month 3 cost',
  },
] as const

type Costs = Record<(typeof COST_FIELDS)[number]['key'], number | ''>

const NO_COSTS: Costs = { intake: '', month1: '', month2: '', month3: '' }
const FULLY_COVERED: Costs = { intake: 0, month1: 0, month2: 0, month3: 0 }

const toCents = (dollars: number | '') => Math.round(Number(dollars) * 100)
const toDollars = (cents: number) => Math.round(cents) / 100

// Standard's `decimalDigitsMaxTwo`, which also refuses a negative amount.
const validAmount = (amount: number | '') => amount === '' || /^\d+([.,]\d{0,2})?$/.test(String(amount))

const amountAt = (source: unknown, path: string): number | '' => {
  const found = getIn(source, path)
  const amount = typeof found === 'string' && found.trim() !== '' ? Number(found) : found

  return typeof amount === 'number' && Number.isFinite(amount) ? amount : ''
}

const costDescription = (costs: Costs) =>
  COST_FIELDS.map(({ key, descriptionLabel }) => `${descriptionLabel}: ${costs[key]}$`).join('\n')

const insuranceFromCheck = ({ coverageActive, subscriber, aperoEligibilityID, warnings }: EligibilityCheck) => ({
  provided: true,
  coverageActive,
  memberID: subscriber.id ?? '',
  groupDescription: subscriber.groupDescription ?? '',
  groupNumber: subscriber.groupNumber ?? '',
  firstname: subscriber.firstName ?? '',
  lastname: subscriber.lastName ?? '',
  birthDate: isoDay(subscriber.birthDate),
  aperoEligibilityID,
  warnings: warnings ?? [],
})

// As Standard: the latest check's subscriber, else the patient's name, ticked when they have a credentialing plan.
const initialInsurance = (patient: Patient): Insurance =>
  patient?.financialEligibilityCheck
    ? insuranceFromCheck(patient.financialEligibilityCheck)
    : {
        provided: Boolean(patient?.credentialingHealthPlan),
        coverageActive: false,
        memberID: '',
        groupDescription: '',
        groupNumber: '',
        firstname: patient?.firstname ?? '',
        lastname: patient?.lastname ?? '',
        birthDate: isoDay(patient?.birthDate),
        warnings: [],
      }

const costsFromDraft = (draft: Record<string, unknown>): Costs => ({
  intake: amountAt(draft, 'value.costEstimate.intakeCostEstimate'),
  month1: amountAt(draft, 'value.costEstimate.month1CostEstimate'),
  month2: amountAt(draft, 'value.costEstimate.month2CostEstimate'),
  month3: amountAt(draft, 'value.costEstimate.month3CostEstimate'),
})

// Standard's Formik values for this form, so a draft started on either dashboard opens on the other.
const toDraft = (date: string, description: string, costs: Costs, insurance: Insurance) => ({
  date,
  type: FORM_TYPE,
  value: {
    description,
    insuranceSubscriber: {
      insuranceSubscriberProvided: insurance.provided,
      coverageActive: insurance.coverageActive,
      memberID: insurance.memberID,
      groupDescription: insurance.groupDescription,
      groupNumber: insurance.groupNumber,
      firstname: insurance.firstname,
      lastname: insurance.lastname,
      birthDate: insurance.birthDate,
    },
    costEstimate: Object.fromEntries(COST_FIELDS.map(({ key, standardKey }) => [standardKey, costs[key]])),
  },
})

const SectionActions = ({ children }: { children: React.ReactNode }) => (
  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>{children}</Box>
)

const Warnings = ({ warnings }: { warnings: string[] }) => (
  <Box>
    <Typography variant="overline">Warnings</Typography>
    {warnings.length ? (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 0.5 }}>
        {warnings.map((warning) => (
          <Chip key={warning} label={warning} color="warning" size="small" />
        ))}
      </Box>
    ) : (
      <Typography variant="body2">—</Typography>
    )}
  </Box>
)

const EligibilityForm = ({ uid, patient, loadError }: { uid: string; patient: Patient; loadError: unknown }) => {
  const restored = useRestoredDraft(uid, FORM_TYPE)
  const [date, setDate] = useState(textAt(restored, 'date') || todayIso())
  const [description, setDescription] = useState(textAt(restored, 'value.description'))
  const [costs, setCosts] = useState<Costs>(() => (restored ? costsFromDraft(restored) : NO_COSTS))
  // As Standard: a draft keeps its date, description and amounts, and insurance always comes from the latest check.
  const [insurance, setInsurance] = useState(() => initialInsurance(patient))
  const [checked, setChecked] = useState(false)
  const [lookupOpen, setLookupOpen] = useState(false)
  const actionsDisabled = useDisabledInPreview()
  const { addToast } = useToasts()
  const [lookupCostEstimate, costEstimateLookup] = useAdvancedCostEstimateLookupLazyQuery({
    fetchPolicy: 'network-only',
  })
  const { submit, submitting, error } = useSubmitFinancialEligibilityHealthPlan(uid)
  useDraftSaver(uid, FORM_TYPE, toDraft(date, description, costs, insurance), submitting)

  const setInsuranceField =
    <K extends keyof Insurance>(key: K) =>
    (value: Insurance[K]) =>
      setInsurance((current) => ({ ...current, [key]: value }))

  const fillCosts = (next: Costs, nextDescription = costDescription(next)) => {
    setCosts(next)
    setDescription(nextDescription)
  }

  const fillFromLookup = (check: EligibilityCheck) => {
    setInsurance(insuranceFromCheck(check))
    setLookupOpen(false)
  }

  const pasteFromClipboard = async () => {
    const pasted = await getCostEstimateValuesFromClipboard()

    if (!pasted) {
      addToast(
        'Could not read cost estimation values from the clipboard. Make sure that you have copied the values to the clipboard',
        { appearance: 'error' }
      )
      return
    }

    fillCosts({
      intake: pasted.intakeCostEstimate ?? '',
      month1: pasted.month1CostEstimate ?? '',
      month2: pasted.month2CostEstimate ?? '',
      month3: pasted.month3CostEstimate ?? '',
    })
  }

  const getCostEstimate = async () => {
    const { data, error: lookupError } = await lookupCostEstimate({ variables: { uid } })
    const estimate = data?.costEstimateLookup

    if (!estimate || lookupError) {
      addToast(<FailureMessage failure={describeFailure('Failed to get a cost estimate.', lookupError)} />, {
        appearance: 'error',
      })
      return
    }

    if (estimate.__typename === 'CostEstimateLookupFailure') {
      addToast(`Cost estimation failed: ${estimate.reason}`, { appearance: 'warning' })
      return
    }

    addToast('Cost estimation succeeded.', { appearance: 'success' })
    fillCosts({
      intake: toDollars(estimate.intakeCostEstimateInCents),
      month1: toDollars(estimate.month1CostEstimateInCents),
      month2: toDollars(estimate.month2CostEstimateInCents),
      month3: toDollars(estimate.month3CostEstimateInCents),
    })
  }

  const required = useRequiredFields({
    'Date of event': Boolean(date),
    ...Object.fromEntries(COST_FIELDS.map(({ key, label }) => [label, costs[key] !== ''])),
    Description: Boolean(description.trim()),
    ...(insurance.provided && {
      'Member ID': Boolean(insurance.memberID.trim()),
      'First name': Boolean(insurance.firstname.trim()),
      'Last name': Boolean(insurance.lastname.trim()),
    }),
  })

  const problems: Partial<Record<string, string>> = {
    'Coverage active': insurance.provided && !insurance.coverageActive ? COVERAGE_INACTIVE : undefined,
    ...Object.fromEntries(
      COST_FIELDS.map(({ key, label }) => [label, validAmount(costs[key]) ? undefined : TOO_MANY_DECIMALS])
    ),
  }
  const issues = Object.entries(problems).flatMap(([label, problem]) => (problem ? [`${label}: ${problem}`] : []))
  const problemAt = (label: string) => (checked ? problems[label] : undefined)

  const onSubmit = async () => {
    const complete = required.attempt()
    setChecked(true)

    if (!complete || issues.length > 0) {
      return
    }

    const { firstname, lastname, memberID, coverageActive, groupNumber, groupDescription, birthDate } = insurance
    await submit({
      date,
      description,
      costEstimate: {
        intakeCostEstimateInCents: toCents(costs.intake),
        month1CostEstimateInCents: toCents(costs.month1),
        month2CostEstimateInCents: toCents(costs.month2),
        month3CostEstimateInCents: toCents(costs.month3),
      },
      insuranceSubscriber: insurance.provided
        ? { firstname, lastname, memberID, coverageActive, groupNumber, groupDescription, birthDate }
        : undefined,
    })
    setDescription('')
    required.reset()
    setChecked(false)
  }

  const costCalculatorUrl = `${COST_CALCULATOR_URL}#${qs.stringify(
    { eligibilityID: insurance.aperoEligibilityID, healthplan: patient?.credentialingHealthPlan },
    { skipNulls: true }
  )}`

  return (
    <FieldSection>
      {loadError ? (
        <Alert severity="error">
          <FailureMessage
            failure={describeFailure('Failed to load eligibility inputs, so the form is not prefilled.', loadError)}
          />
        </Alert>
      ) : null}
      <FieldGrid>
        <LabeledDate
          label="Date of event"
          value={date}
          onChange={setDate}
          required
          error={required.error('Date of event')}
        />
      </FieldGrid>

      <FieldSection title="Insurance details">
        <SectionActions>
          {insurance.aperoEligibilityID && (
            <Button
              variant="outlined"
              href={aperoEligibilityUrl(insurance.aperoEligibilityID)}
              target="_blank"
              rel="noreferrer"
            >
              Open details in Apero
            </Button>
          )}
          <Button variant="outlined" onClick={() => setLookupOpen(true)} disabled={actionsDisabled}>
            Lookup tool
          </Button>
        </SectionActions>
        <HealthPlanLookupDialog
          uid={uid}
          open={lookupOpen}
          onClose={() => setLookupOpen(false)}
          onFill={fillFromLookup}
        />
        <LabeledCheckbox
          label="Provide insurance details"
          checked={insurance.provided}
          onChange={setInsuranceField('provided')}
        />
        {insurance.provided && (
          <>
            <LabeledCheckbox
              label="Coverage active"
              checked={insurance.coverageActive}
              onChange={setInsuranceField('coverageActive')}
              error={problemAt('Coverage active')}
            />
            <FieldGrid>
              <LabeledText
                label="Member ID"
                value={insurance.memberID}
                onChange={setInsuranceField('memberID')}
                required
                error={required.error('Member ID')}
              />
              <LabeledText
                label="Group description"
                value={insurance.groupDescription}
                onChange={setInsuranceField('groupDescription')}
                disabled
              />
              <LabeledText
                label="Group number"
                value={insurance.groupNumber}
                onChange={setInsuranceField('groupNumber')}
              />
              <LabeledText
                label="First name"
                value={insurance.firstname}
                onChange={setInsuranceField('firstname')}
                required
                error={required.error('First name')}
              />
              <LabeledText
                label="Last name"
                value={insurance.lastname}
                onChange={setInsuranceField('lastname')}
                required
                error={required.error('Last name')}
              />
              <LabeledDate label="Birthdate" value={insurance.birthDate} onChange={setInsuranceField('birthDate')} />
            </FieldGrid>
            <Warnings warnings={insurance.warnings} />
          </>
        )}
      </FieldSection>

      <FieldSection title="Cost estimate">
        {patient?.costEstimateFailure && (
          <Alert severity="warning">
            <FailureMessage
              failure={{
                summary: 'Automatic cost estimation warning',
                reason: `Reason: ${patient.costEstimateFailure.reason}`,
              }}
            />
          </Alert>
        )}
        <SectionActions>
          <Button variant="outlined" href={costCalculatorUrl} target="_blank" rel="noreferrer">
            Open cost calculator
          </Button>
          <Button variant="outlined" onClick={pasteFromClipboard} disabled={actionsDisabled}>
            Paste from clipboard
          </Button>
          <Button
            variant="outlined"
            onClick={() => fillCosts(FULLY_COVERED, 'Eligible, no cost')}
            disabled={actionsDisabled}
          >
            Fully covered
          </Button>
          <Button variant="outlined" onClick={getCostEstimate} disabled={actionsDisabled || costEstimateLookup.loading}>
            Get cost estimate
          </Button>
        </SectionActions>
        <FieldGrid>
          {COST_FIELDS.map(({ key, label }) => (
            <LabeledNumber
              key={key}
              label={label}
              value={costs[key]}
              onChange={(value) => setCosts((current) => ({ ...current, [key]: value }))}
              required
              error={required.error(label) ?? problemAt(label)}
            />
          ))}
        </FieldGrid>
        <LabeledText
          label="Description"
          value={description}
          onChange={setDescription}
          multiline
          required
          error={required.error('Description')}
        />
      </FieldSection>

      <MissingFields labels={required.missing} />
      <ValidationIssues issues={checked ? issues : []} />
      <FormActions onSubmit={onSubmit} disabled={false} submitting={submitting} error={error} />
    </FieldSection>
  )
}

// Not a real `PatientHistoryType`: a form-only choice submitted through
// `addFinancialEligibilityPatientHistory`. Dollars in the form, cents in the mutation.
const FinancialEligibilityHealthPlanFields = ({ uid }: { uid: string }) => {
  // `all`: a refused patient read still opens the form, just without its prefill.
  const { data, loading, error } = useGetAdvancedEligibilityLookupInputsQuery({
    variables: { uid },
    errorPolicy: 'all',
  })
  const loaded = useLoadedNoteDraft({ uid, type: FORM_TYPE })

  if (loading || !loaded.ready) {
    return <PrefillLoading />
  }

  primeRestoredDraft({ uid, type: FORM_TYPE, value: loaded.draft })

  return (
    <>
      <NoteDraftAlerts uid={uid} formType={FORM_TYPE} />
      <EligibilityForm uid={uid} patient={data?.patient} loadError={error} />
    </>
  )
}

export default FinancialEligibilityHealthPlanFields
